import React, { useEffect, useMemo, useState } from "react";
import unsplash from "../api/unsplash";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Tooltip,
  Checkbox,
  Chip,
  Select,
  Card,
  Grid,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Zoom,
  LinearProgress,
  TextField,
  Autocomplete,
  Slider,
} from "@mui/material";
import {
  DataGridPro,
  itIT,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
} from "../utils/PersistedDataGridPro";
import { CustomFooterPersonalized } from "../utils/CustomFooterPersonalized";
import {
  Add,
  Circle,
  Search,
  HideSource,
  Delete,
  Person,
  PersonOff,
  Close,
  ConnectingAirportsOutlined,
  Edit,
  Flight,
} from "@mui/icons-material";
import GlobalSnackbar from "./Snackbar";
import Allegati from "./Allegati";
import moment from "moment";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { openPdfDocument } from "../utils/openPdfDocument";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom timeout={2000} in={true} ref={ref} {...props} />;
});

const normalizeCourseDateKey = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const normalizeCompanySearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const dedupeCourseOptions = (courses = []) => {
  const byKey = new Map();
  courses.forEach((c) => {
    const tipo = `${c?.TipoCodice || c?.Tipo || ""}`.trim();
    const agg = Number(c?.Aggiornamento || 0);
    const dateKey = normalizeCourseDateKey(c?.DataInizio || c?.DataCorso);
    const key = `${tipo}|${agg}|${dateKey || "no-date"}`;
    const prev = byKey.get(key);
    if (!prev || Number(c?.ID || 0) > Number(prev?.ID || 0)) {
      byKey.set(key, c);
    }
  });
  return Array.from(byKey.values());
};

const buildAggregateGroupsForImport = (files = [], directives = {}) => {
  const aggregateEntries = (files || []).filter((f) => {
    const d = directives[f.fileId] || {};
    return d.include !== false && !!d.aggregate;
  });
  const bySource = new Map();
  aggregateEntries.forEach((entry) => {
    const source = String(
      entry?.sourceOriginalName || entry?.originalName || "",
    ).trim();
    if (!source) return;
    if (!bySource.has(source)) bySource.set(source, []);
    bySource.get(source).push(entry);
  });

  const groups = [];
  Array.from(bySource.entries()).forEach(([source, sourceEntries]) => {
    const ordered = (sourceEntries || []).sort((a, b) => {
      const pa = Number(a?.sourcePage || 0);
      const pb = Number(b?.sourcePage || 0);
      if (pa && pb) return pa - pb;
      return String(a?.fileId || "").localeCompare(String(b?.fileId || ""));
    });

    let currentGroup = null;
    let progressive = 0;
    ordered.forEach((item) => {
      const workerId = Number(item?.selectedLavoratore?.Lavoratore || 0);
      if (workerId > 0) {
        if (!currentGroup || currentGroup.workerId !== workerId) {
          progressive += 1;
          currentGroup = {
            key: `source:${source}:worker:${workerId}:chunk:${progressive}`,
            workerId,
            items: [],
          };
          groups.push(currentGroup);
        }
        currentGroup.items.push(item);
      } else if (currentGroup) {
        currentGroup.items.push(item);
      }
    });
  });

  return groups;
};

const buildRigaAttestatoManuale = (id) => ({
  id,
  tipoCorso: null,
  aggiornamento: false,
  modalita: "corso",
  data: "",
});

// Replica la regola della vista Corsi_Base. ScadenzaPersonalizzata dipende dal
// cliente e non e nell'anagrafica corsi: in quel caso il calcolo lo fa il backend.
const anniValiditaAttestatoManuale = (tipoCorso) => {
  if (!tipoCorso) return null;
  if (Number(tipoCorso?.PeriodicitaAggiornamento || 0) !== 0) return null;
  return Number(tipoCorso?.Scadenza || 0);
};

const labelLavoratoreAttestatoManuale = (lavoratore) => {
  if (!lavoratore) return "";
  const nominativo =
    `${lavoratore?.Cognome || ""} ${lavoratore?.Nome || ""}`.trim();
  const parti = [nominativo || `ID ${lavoratore?.ID || ""}`];
  const cf = String(lavoratore?.CF || "").toUpperCase();
  if (cf) parti.push(cf);
  return parti.join(" - ");
};

const labelTipoCorsoAttestatoManuale = (tipoCorso) => {
  if (!tipoCorso) return "";
  const codice = String(tipoCorso?.Codice || "").trim();
  const descrizione = String(tipoCorso?.Descrizione || "").trim();
  return descrizione ? `${codice} - ${descrizione}` : codice;
};

const anteprimaAttestatoManuale = (riga) => {
  const data = moment(riga?.data, "YYYY-MM-DD", true);
  if (!riga?.tipoCorso || !data.isValid()) return "";
  const anni = anniValiditaAttestatoManuale(riga.tipoCorso);
  if (anni === null) return "Calcolata al salvataggio";
  if (riga.modalita === "scadenza") {
    if (!anni) return "Nessuna scadenza: usa la data corso";
    return `Corso: ${data.clone().subtract(anni, "years").format("DD/MM/YYYY")}`;
  }
  if (!anni) return "Formazione permanente";
  return `Scade: ${data.clone().add(anni, "years").format("DD/MM/YYYY")}`;
};

const Scadenzario = () => {
  const apiRef = useGridApiRef();
  const [scadenzeCorsi, setScadenzeCorsi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scadenzeCorsiBackup, setScadenzeCorsiBackup] = useState([]);
  const [filtro, setFiltro] = useState({
    verde: true,
    arancione: true,
    rosso: true,
    viola: true,
    blue: true,
    attivi: true,
    passivi: false,
  });
  const [mesiScadenza, setMesiScadenza] = useState(0);
  const [mostraTutto, setMostraTutto] = useState(false);
  const [filtroDate, setFiltroDate] = useState(() => ({
    dal: moment().startOf("year").format("YYYY-MM-DD"),
    al: moment().endOf("year").format("YYYY-MM-DD"),
  }));
  const [filtroDateInput, setFiltroDateInput] = useState(() => ({
    dal: moment().startOf("year").format("YYYY-MM-DD"),
    al: moment().endOf("year").format("YYYY-MM-DD"),
  }));
  const [corsi, setCorsi] = useState([]);
  const [corsiSel, setCorsiSel] = useState([]);
  const [lavoratore, setLavoratore] = useState(0);
  const [loaded, setLoaded] = useState(true);
  const [openImportAttestatiMassivi, setOpenImportAttestatiMassivi] =
    useState(false);
  const [openRiconciliazioneAttestati, setOpenRiconciliazioneAttestati] =
    useState(false);
  const [clienteRiconciliazione, setClienteRiconciliazione] = useState(null);
  const [lavoratoreRiconciliazione, setLavoratoreRiconciliazione] =
    useState(null);
  const [righeRiconciliazione, setRigheRiconciliazione] = useState([]);
  const [selezioneRiconciliazione, setSelezioneRiconciliazione] = useState([]);
  const [loadingRiconciliazione, setLoadingRiconciliazione] = useState(false);
  const [applyRiconciliazioneEnabled, setApplyRiconciliazioneEnabled] =
    useState(false);
  const [canManageRiconciliazione, setCanManageRiconciliazione] =
    useState(false);
  const [storicoRiconciliazione, setStoricoRiconciliazione] = useState([]);
  const [clientiRiconciliazioneApi, setClientiRiconciliazioneApi] = useState(
    [],
  );
  const [lavoratoriRiconciliazioneApi, setLavoratoriRiconciliazioneApi] =
    useState([]);
  const [mostraCoerentiRiconciliazione, setMostraCoerentiRiconciliazione] =
    useState(false);
  const [filesAttestatiMassivi, setFilesAttestatiMassivi] = useState([]);
  const [analisiAttestatiMassivi, setAnalisiAttestatiMassivi] = useState([]);
  const [jobAttestatiMassivi, setJobAttestatiMassivi] = useState("");
  const [dateChoicesAttestatiMassivi, setDateChoicesAttestatiMassivi] =
    useState({});
  const [pageDirectivesAttestatiMassivi, setPageDirectivesAttestatiMassivi] =
    useState({});
  const [loadingAnalyzeAttestatiMassivi, setLoadingAnalyzeAttestatiMassivi] =
    useState(false);
  const [loadingConfirmAttestatiMassivi, setLoadingConfirmAttestatiMassivi] =
    useState(false);
  const [corsiImportOptions, setCorsiImportOptions] = useState([]);
  const [tipiCorsoImportOptions, setTipiCorsoImportOptions] = useState([]);
  const [tipiCorsoImportFilteredOptions, setTipiCorsoImportFilteredOptions] =
    useState([]);
  const [
    suggestedCoursesAttestatiMassivi,
    setSuggestedCoursesAttestatiMassivi,
  ] = useState([]);
  const [
    suggestedCourseIdAttestatiMassivi,
    setSuggestedCourseIdAttestatiMassivi,
  ] = useState("");
  const [corsoImportSel, setCorsoImportSel] = useState([]);
  const [tipoCorsoImportSel, setTipoCorsoImportSel] = useState("");
  const [
    confirmAnomaliesAttestatiMassivi,
    setConfirmAnomaliesAttestatiMassivi,
  ] = useState([]);
  const [openAttestatoManuale, setOpenAttestatoManuale] = useState(false);
  const [lavoratoriAttestatoManuale, setLavoratoriAttestatoManuale] = useState(
    [],
  );
  const [
    loadingLavoratoriAttestatoManuale,
    setLoadingLavoratoriAttestatoManuale,
  ] = useState(false);
  const [aziendaAttestatoManuale, setAziendaAttestatoManuale] = useState(null);
  const [lavoratoreAttestatoManuale, setLavoratoreAttestatoManuale] =
    useState(null);
  const [righeAttestatoManuale, setRigheAttestatoManuale] = useState([
    buildRigaAttestatoManuale(1),
  ]);
  const [progressivoRigaAttestatoManuale, setProgressivoRigaAttestatoManuale] =
    useState(1);
  const [loadingSaveAttestatoManuale, setLoadingSaveAttestatoManuale] =
    useState(false);
  const [esitiAttestatoManuale, setEsitiAttestatoManuale] = useState({});
  const [openSnack, setOpenSnack] = useState(false);
  const [snack, setSnack] = useState({
    severity: "success",
    messaggio: "Operazione completata",
  });

  const columns = [
    {
      field: "Actions",
      headerName: "Azioni",
      hide: false,
      width: 60,
      headerAlign: "center",
      align: "center",
      renderCell: (
        params, //mi aiuta a mettere il valore
      ) => (
        <>
          <IconButton
            onClick={async () => {
              await setLavoratore(params.row.Lavoratore);
              document.getElementById("allegati").click();
            }}
          >
            <Edit style={{ height: "2rem", width: "2rem", color: "#2265B1" }} />
          </IconButton>
        </>
      ),
    },
    {
      field: "D",
      headerName: "D",
      width: 10,

      renderCell: (
        params, //mi aiuta a mettere il valore
      ) => (
        <div>
          {params.row.RagioneSocialeDa ? (
            <Tooltip title={"Distaccato da " + params.row.RagioneSocialeDa}>
              <Flight
                className="hover:cursor-pointer"
                sx={{ rotate: "135deg", color: "#9059C3" }}
              />
            </Tooltip>
          ) : (
            ""
          )}
        </div>
      ),
      ///<Flight  sx={{rotate:"135deg",, color: "#9059C3"}}/>
    },
    {
      filterable: false,
      field: "DataTermine",
      headerName: "Attivo",
      width: 60,
      renderCell: (
        params, //mi aiuta a mettere il valore
      ) => {
        let attivo =
          !params.row.DataTermine ||
          moment(params.row.DataTermine).format("YYYY-MM-DD") >
            moment(new Date()).format("YYYY-MM-DD");

        return (
          <div>
            {attivo ? (
              <Tooltip title="Attivo">
                <Person sx={{ color: "#40C18F" }} />
              </Tooltip>
            ) : (
              <Tooltip title="Cessato">
                <PersonOff sx={{ color: "#FF2768" }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "Cliente",
      headerName: "Cliente",
      //width: 400,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
    {
      field: "Cognome",
      headerName: "Cognome",
      //width: 400,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
    {
      field: "Nome",
      headerName: "Nome",
      //width: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
    {
      field: "CF",
      headerName: "Codice fiscale",
      width: 170,
      headerAlign: "center",
      align: "center",
      sort: "asc",
      valueGetter: (params) => String(params.row?.CF || "").toUpperCase(),
      renderCell: (params) => String(params.row?.CF || "").toUpperCase(),
    },
    {
      field: "*",
      headerName: "Corsi",
      //width: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
      valueGetter: (params, row) => {
        let scadenze = "";
        params.row?.Corsi.map((c) => {
          if (corsiSel.length > 0 && corsiSel.includes(c.Ruolo)) {
            scadenze += `${c.Ruolo}: ${c.Scadenza} `;
          } else if (corsiSel.length === 0) {
            scadenze += `${c.Ruolo}: ${c.Scadenza} `;
          }
        });
        return scadenze;
      },
      renderCell: (params) => {
        return (
          <div>
            {params.row.Corsi.map((c) => {
              let colore = "",
                testo = "";
              let date = new Date();
              let show = true;
              // add a day
              date.setDate(date.getDate() + 90);
              if (c.BaseOk === 0) {
                colore = "#8E75AC";
                testo = "Corso Base mancante";
                if (filtro.viola === true) {
                  show = true;
                } else {
                  show = false;
                }
              } else if (c.BaseOk === 0 && c.Scadenza !== "") {
                colore = "#11526f";
                testo = "Aggiornamento senza corso base";
                if (filtro.blue === true) {
                  show = true;
                } else {
                  show = false;
                }
              } else if (c.Scadenza === "") {
                colore = "#40C18F";
                testo = "Nessuna scadenza";
                if (filtro.verde === true) {
                  show = true;
                } else {
                  show = false;
                }
              } else if (
                moment(c.Scadenza).format("YYYY-MM-DD") <
                moment(new Date()).format("YYYY-MM-DD")
              ) {
                colore = "#FF2768";
                testo = "Scaduto il " + moment(c.Scadenza).format("DD/MM/YYYY");

                if (filtro.rosso === true) {
                  show = true;
                } else {
                  show = false;
                }
              } else if (
                moment(c.Scadenza).format("YYYY-MM-DD") <
                moment(date).format("YYYY-MM-DD")
              ) {
                colore = "#FFC897";
                testo =
                  "In scadenza il " + moment(c.Scadenza).format("DD/MM/YYYY");
                if (filtro.arancione === true) {
                  show = true;
                } else {
                  show = false;
                }
              } else {
                colore = "#40C18F";
                testo = "Scadrà il " + moment(c.Scadenza).format("DD/MM/YYYY");
                if (filtro.verde === true) {
                  show = true;
                } else {
                  show = false;
                }
              }
              let showCorso = true;
              if (corsiSel.length > 0 && !corsiSel.includes(c.Ruolo)) {
                showCorso = false;
              }
              if (show && showCorso) {
                return (
                  <Tooltip title={testo}>
                    <Chip
                      label={c.Ruolo}
                      size="small"
                      sx={{
                        width: "70px",
                        marginLeft: "2px",
                        backgroundColor:
                          c.BaseOk === 0 && c.Scadenza !== "" && filtro.blue
                            ? "#11526f"
                            : colore,
                        color: "white",
                      }}
                    />
                  </Tooltip>
                );
              }
            })}
          </div>
        );
      },
    },
    {
      field: "Descrizione",
      headerName: "Mansione",
      //width: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
    {
      field: "MansioniSicurezza",
      headerName: "MansioniSicurezza",
      //width: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
    {
      field: "Note",
      headerName: "Note",
      //width: 300,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
    {
      field: "Sedi",
      headerName: "Sede",
      //width: 400,
      flex: 1,
      headerAlign: "center",
      align: "center",
      sort: "asc",
    },
  ];

  const csvFileName = mostraTutto
    ? "Scadenzario"
    : `Scadenzario dal ${moment(filtroDate.dal).format(
        "DD-MM-YYYY",
      )} al ${moment(filtroDate.al).format("DD-MM-YYYY")}${
        mesiScadenza ? ` entro ${mesiScadenza} mesi` : ""
      }`;

  const getExportRows = () => {
    const enabledColors = [
      "verde",
      "arancione",
      "rosso",
      "viola",
      "blue",
    ].filter((color) => filtro[color]);
    const start = filtroDate.dal ? moment(filtroDate.dal, "YYYY-MM-DD") : null;
    const end = filtroDate.al ? moment(filtroDate.al, "YYYY-MM-DD") : null;

    const filteredWorkers = gridFilteredSortedRowIdsSelector(apiRef).map((id) =>
      apiRef.current.getRow(id),
    );
    return filteredWorkers.flatMap((worker) =>
      (worker.Corsi || [])
        .filter((course) => {
          if (corsiSel.length && !corsiSel.includes(course.Ruolo)) return false;
          if (course.Colore && !enabledColors.includes(course.Colore))
            return false;
          if (mostraTutto) return true;
          if (!course.Scadenza) return false;
          const expiry = moment(course.Scadenza);
          if (!expiry.isValid()) return false;
          if (start && expiry.isBefore(start, "day")) return false;
          if (end && expiry.isAfter(end, "day")) return false;
          return true;
        })
        .map((course) => {
          let statoCorso = "Regolare";
          if (course.BaseOk === 0 && course.Scadenza) {
            statoCorso = "Aggiornamento senza corso base";
          } else if (course.BaseOk === 0) {
            statoCorso = "Corso base mancante";
          } else if (!course.Scadenza) {
            statoCorso = "Nessuna scadenza";
          } else if (moment(course.Scadenza).isBefore(moment(), "day")) {
            statoCorso = "Scaduto";
          } else if (
            moment(course.Scadenza).isSameOrBefore(
              moment().add(90, "days"),
              "day",
            )
          ) {
            statoCorso = "In scadenza";
          }

          const attivo =
            !worker.DataTermine ||
            moment(worker.DataTermine).isAfter(moment(), "day");
          return {
            cliente: worker.Cliente || "",
            cognome: worker.Cognome || "",
            nome: worker.Nome || "",
            cf: String(worker.CF || "").toUpperCase(),
            lavoratoreAttivo: attivo ? "Attivo" : "Cessato",
            dataTermine: worker.DataTermine || "",
            sede: worker.Sedi || "",
            mansione: worker.Descrizione || "",
            mansioniSicurezza: worker.MansioniSicurezza || "",
            corso: course.Ruolo || "",
            scadenza: course.Scadenza || "",
            statoCorso,
            note: worker.Note || "",
            distaccatoDa: worker.RagioneSocialeDa || "",
          };
        }),
    );
  };

  const getExportSubtitle = () => {
    if (mostraTutto) return "Tutte le scadenze";
    const interval = `Dal ${moment(filtroDate.dal).format("DD/MM/YYYY")} al ${moment(
      filtroDate.al,
    ).format("DD/MM/YYYY")}`;
    return mesiScadenza ? `${interval} (${mesiScadenza} mesi)` : interval;
  };

  const exportScadenzarioExcel = async () => {
    const rows = getExportRows();
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Koala";
    workbook.created = new Date();
    const sheet = workbook.addWorksheet("Scadenze", {
      views: [{ state: "frozen", ySplit: 4 }],
    });
    const headers = [
      "Cliente",
      "Cognome",
      "Nome",
      "Codice fiscale",
      "Stato lavoratore",
      "Data termine",
      "Sede",
      "Mansione",
      "Mansioni sicurezza",
      "Corso",
      "Scadenza",
      "Stato corso",
      "Note",
      "Distaccato da",
    ];
    sheet.mergeCells(1, 1, 1, headers.length);
    sheet.getCell("A1").value = "SCADENZARIO FORMAZIONE";
    sheet.getCell("A1").font = {
      bold: true,
      size: 18,
      color: { argb: "FFFFFFFF" },
    };
    sheet.getCell("A1").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF11526F" },
    };
    sheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    sheet.getRow(1).height = 28;
    sheet.mergeCells(2, 1, 2, headers.length);
    sheet.getCell("A2").value =
      `${getExportSubtitle()} · ${rows.length} scadenze`;
    sheet.getCell("A2").font = { italic: true, color: { argb: "FF44546A" } };
    sheet.getCell("A2").alignment = { horizontal: "center" };
    sheet.addRow([]);
    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF2265B1" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      cell.border = { bottom: { style: "thin", color: { argb: "FFFFFFFF" } } };
    });
    rows.forEach((row, index) => {
      const excelRow = sheet.addRow([
        row.cliente,
        row.cognome,
        row.nome,
        row.cf,
        row.lavoratoreAttivo,
        row.dataTermine ? new Date(row.dataTermine) : null,
        row.sede,
        row.mansione,
        row.mansioniSicurezza,
        row.corso,
        row.scadenza ? new Date(row.scadenza) : null,
        row.statoCorso,
        row.note,
        row.distaccatoDa,
      ]);
      excelRow.alignment = { vertical: "top", wrapText: true };
      if (index % 2) {
        excelRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF2F6FA" },
          };
        });
      }
      const statusCell = excelRow.getCell(12);
      const statusColors = {
        Scaduto: "FFFFC7CE",
        "In scadenza": "FFFFE0B2",
        "Corso base mancante": "FFE4D7F2",
        "Aggiornamento senza corso base": "FFD9EAF2",
      };
      if (statusColors[row.statoCorso]) {
        statusCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: statusColors[row.statoCorso] },
        };
      }
    });
    sheet.getColumn(6).numFmt = "dd/mm/yyyy";
    sheet.getColumn(11).numFmt = "dd/mm/yyyy";
    sheet.autoFilter = {
      from: { row: 4, column: 1 },
      to: { row: 4, column: headers.length },
    };
    [24, 18, 18, 19, 16, 14, 20, 24, 24, 16, 14, 30, 30, 24].forEach(
      (width, index) => {
        sheet.getColumn(index + 1).width = width;
      },
    );
    sheet.pageSetup = {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    };
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${csvFileName}.xlsx`,
    );
  };

  const exportScadenzarioPdf = () => {
    const rows = getExportRows();
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });
    doc.setFontSize(18);
    doc.setTextColor(17, 82, 111);
    doc.text("Scadenzario formazione", 14, 16);
    doc.setFontSize(9);
    doc.setTextColor(70, 70, 70);
    doc.text(`${getExportSubtitle()} · ${rows.length} scadenze`, 14, 23);
    doc.autoTable({
      startY: 28,
      head: [
        [
          "Cliente",
          "Lavoratore",
          "Codice fiscale",
          "Sede",
          "Mansione",
          "Corso",
          "Scadenza",
          "Stato",
          "Note",
        ],
      ],
      body: rows.map((row) => [
        row.cliente,
        `${row.cognome} ${row.nome}`.trim(),
        row.cf,
        row.sede,
        row.mansione,
        row.corso,
        row.scadenza ? moment(row.scadenza).format("DD/MM/YYYY") : "-",
        row.statoCorso,
        row.note,
      ]),
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [17, 82, 111],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [242, 246, 250] },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 38 },
        2: { cellWidth: 32 },
        3: { cellWidth: 32 },
        4: { cellWidth: 42 },
        5: { cellWidth: 28 },
        6: { cellWidth: 25 },
        7: { cellWidth: 42 },
        8: { cellWidth: "auto" },
      },
      didDrawPage: () => {
        const page = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          `Pagina ${page}`,
          doc.internal.pageSize.getWidth() - 28,
          doc.internal.pageSize.getHeight() - 7,
        );
      },
      margin: { top: 14, right: 14, bottom: 14, left: 14 },
    });
    openPdfDocument(doc, `${csvFileName}.pdf`);
  };

  const FooterWithExport = (props) => (
    <CustomFooterPersonalized
      {...props}
      csvOptions={{ fileName: csvFileName }}
      onExportExcel={exportScadenzarioExcel}
      onExportPdf={exportScadenzarioPdf}
    />
  );

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <GridToolbarQuickFilter placeholder="Cerca..." />

          <Tooltip title="Ok">
            <Checkbox
              icon={<HideSource />}
              checked={filtro.verde}
              checkedIcon={<Circle />}
              sx={{ color: "#40C18F!important" }}
              disabled={loading}
              onClick={(e) => filterData(e, "verde", corsiSel)}
            />
          </Tooltip>
          <Tooltip title="In scadenza">
            <Checkbox
              icon={<HideSource />}
              checked={filtro.arancione}
              checkedIcon={<Circle />}
              sx={{ color: "#FFC897!important" }}
              disabled={loading}
              onClick={(e) => filterData(e, "arancione", corsiSel)}
            />
          </Tooltip>
          <Tooltip title="Scaduti">
            <Checkbox
              icon={<HideSource />}
              checked={filtro.rosso}
              checkedIcon={<Circle />}
              sx={{ color: "#FF2768!important" }}
              disabled={loading}
              onClick={(e) => filterData(e, "rosso", corsiSel)}
            />
          </Tooltip>
          <Tooltip title="Corso base mancante">
            <Checkbox
              icon={<HideSource />}
              checked={filtro.viola}
              checkedIcon={<Circle />}
              sx={{ color: "#8E75AC!important" }}
              disabled={loading}
              onClick={(e) => filterData(e, "viola", corsiSel)}
            />
          </Tooltip>
          <Tooltip title="Aggiornamento senza corso base">
            <Checkbox
              icon={<HideSource />}
              checked={filtro.blue}
              checkedIcon={<Circle />}
              sx={{ color: "#11526f!important" }}
              disabled={loading}
              onClick={(e) => filterData(e, "blue", corsiSel)}
            />
          </Tooltip>
          <FormControlLabel
            sx={{ marginBottom: "0px!important" }}
            control={
              <Checkbox
                checked={mostraTutto}
                onChange={handleMostraTutto}
                disabled={loading}
              />
            }
            label="Mostra tutto"
          />
          <FormControlLabel
            sx={{ marginBottom: "0px!important" }}
            control={
              <Checkbox
                checked={filtro.attivi}
                onClick={(e) => filterData(e, "attivi", corsiSel)}
                disabled={loading}
              />
            }
            label="Attivi"
          />
          <FormControlLabel
            sx={{ marginBottom: "0px!important" }}
            control={
              <Checkbox
                checked={filtro.passivi}
                onClick={(e) => filterData(e, "passivi", corsiSel)}
                disabled={loading}
              />
            }
            label="Passivi"
          />
        </Grid>
      </GridToolbarContainer>
    );
  }

  const applyFilters = (source, filtroState, corsi, range, showAll) => {
    let scadenzeTemp = source;
    let checkArr = []; //metto i colori che in base al flag popolo

    if (filtroState.verde) {
      checkArr.push("verde");
    }
    if (filtroState.arancione) {
      checkArr.push("arancione");
    }
    if (filtroState.rosso) {
      checkArr.push("rosso");
    }

    if (filtroState.viola) {
      checkArr.push("viola");
    }

    if (
      filtroState.rosso &&
      filtroState.verde &&
      filtroState.viola &&
      filtroState.arancione &&
      corsi.length === 0
    ) {
    } else {
      if (corsi.length > 0) {
        scadenzeTemp = scadenzeTemp.filter(
          (s) =>
            s.Corsi.filter(
              (sf) => checkArr.includes(sf.Colore) && corsi.includes(sf.Ruolo),
            ).length > 0,
        );
      } else {
        scadenzeTemp = scadenzeTemp.filter(
          (s) =>
            s.Corsi.filter((sf) => checkArr.includes(sf.Colore)).length > 0,
        );
      }
    }

    if (!showAll && (range?.dal || range?.al)) {
      const start = range?.dal ? moment(range.dal, "YYYY-MM-DD") : null;
      const rangeEnd = range?.al ? moment(range.al, "YYYY-MM-DD") : null;
      scadenzeTemp = scadenzeTemp.filter((s) =>
        s.Corsi.some((c) => {
          if (!c.Scadenza) {
            return false;
          }
          const scadenza = moment(c.Scadenza);
          if (!scadenza.isValid()) {
            return false;
          }
          if (start && scadenza.isBefore(start, "day")) return false;
          if (rangeEnd && scadenza.isAfter(rangeEnd, "day")) return false;
          return true;
        }),
      );
    }

    if (!filtroState.attivi) {
      scadenzeTemp = scadenzeTemp.filter(
        (v) =>
          !(
            !v.DataTermine ||
            moment(v.DataTermine).format("YYYY-MM-DD") >
              moment(new Date()).format("YYYY-MM-DD")
          ),
      );
    }
    if (!filtroState.passivi) {
      scadenzeTemp = scadenzeTemp.filter(
        (v) =>
          !v.DataTermine ||
          moment(v.DataTermine).format("YYYY-MM-DD") >
            moment(new Date()).format("YYYY-MM-DD"),
      );
    }

    return scadenzeTemp;
  };

  const filterData = (e, colore, corsi, range, showAll) => {
    let f = { ...filtro };
    if (colore && e?.target) {
      f[colore] = e.target.checked;
      setFiltro(f);
    }
    const scadenzeTemp = applyFilters(
      scadenzeCorsiBackup,
      f,
      corsi,
      range || filtroDate,
      showAll ?? mostraTutto,
    );
    setScadenzeCorsi(scadenzeTemp);
  };

  const fetchScadenzario = async () => {
    try {
      setLoading(true);
      let response = await unsplash.get("scadenzario", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      });
      const data = (response.data.data || []).map((row) => ({
        ...row,
        CF: String(row.CF || "").toUpperCase(),
      }));

      setScadenzeCorsiBackup(data);
      setScadenzeCorsi(
        applyFilters(data, filtro, corsiSel, filtroDate, mostraTutto),
      );
      setLoading(false);
    } catch (error) {
      console.error(error.message);
      setLoading(false);
    }
  };

  const clientiRiconciliazioneFallback = useMemo(() => {
    const byId = new Map();
    scadenzeCorsiBackup.forEach((row) => {
      const id = Number(row.ClienteID || 0);
      if (id && !byId.has(id)) {
        byId.set(id, { id, label: row.Cliente || `Cliente ${id}` });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [scadenzeCorsiBackup]);
  const clientiRiconciliazione = clientiRiconciliazioneApi.length
    ? clientiRiconciliazioneApi
    : clientiRiconciliazioneFallback;

  const lavoratoriRiconciliazioneFallback = useMemo(() => {
    if (!clienteRiconciliazione?.id) return [];
    return scadenzeCorsiBackup
      .filter((row) => Number(row.ClienteID || 0) === clienteRiconciliazione.id)
      .map((row) => ({
        id: Number(row.Lavoratore),
        label: `${row.Cognome || ""} ${row.Nome || ""}`.trim(),
        cf: String(row.CF || "").toUpperCase(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clienteRiconciliazione, scadenzeCorsiBackup]);
  const lavoratoriRiconciliazione = lavoratoriRiconciliazioneApi.length
    ? lavoratoriRiconciliazioneApi
    : lavoratoriRiconciliazioneFallback;

  const fetchOpzioniRiconciliazione = async (clienteId = 0) => {
    try {
      const response = await unsplash.get(
        "corsi/attestatiRiconciliazione/options",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          params: clienteId ? { cliente: clienteId } : {},
        },
      );
      if (clienteId) {
        setLavoratoriRiconciliazioneApi(
          (response?.data?.workers || []).map((row) => ({
            id: Number(row.ID),
            label: `${row.Cognome || ""} ${row.Nome || ""}`.trim(),
            cf: String(row.CF || "").toUpperCase(),
          })),
        );
      } else {
        setClientiRiconciliazioneApi(
          (response?.data?.clients || []).map((row) => ({
            id: Number(row.ID),
            label: row.RagioneSociale || `Cliente ${row.ID}`,
          })),
        );
      }
    } catch (_error) {
      if (clienteId) setLavoratoriRiconciliazioneApi([]);
      else setClientiRiconciliazioneApi([]);
    }
  };

  const analyzeRiconciliazioneAttestati = async () => {
    if (!clienteRiconciliazione?.id) return;
    setLoadingRiconciliazione(true);
    setSelezioneRiconciliazione([]);
    try {
      const response = await unsplash.get(
        "corsi/attestatiRiconciliazione/analyze",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
          params: {
            cliente: clienteRiconciliazione.id,
            lavoratore: lavoratoreRiconciliazione?.id || undefined,
          },
        },
      );
      const rows = response?.data?.data || [];
      setRigheRiconciliazione(rows);
      setApplyRiconciliazioneEnabled(!!response?.data?.applyEnabled);
      setCanManageRiconciliazione(!!response?.data?.canManage);
    } catch (error) {
      setSnack({
        severity: "error",
        messaggio:
          error?.response?.data?.error || "Errore durante l'analisi attestati",
      });
      setOpenSnack(true);
    } finally {
      setLoadingRiconciliazione(false);
    }
  };

  const applyRiconciliazioneAttestati = async () => {
    if (!clienteRiconciliazione?.id || !selezioneRiconciliazione.length) return;
    const numeroCorrezioni = selezioneRiconciliazione.length;
    if (
      !window.confirm(
        "Applicare " +
          numeroCorrezioni +
          (numeroCorrezioni === 1 ? " correzione" : " correzioni") +
          " ai dati di produzione? Ogni operazione verra registrata e potra essere ripristinata dallo storico.",
      )
    ) {
      return;
    }
    setLoadingRiconciliazione(true);
    try {
      const response = await unsplash.post(
        "corsi/attestatiRiconciliazione/apply",
        {
          cliente: clienteRiconciliazione.id,
          items: selezioneRiconciliazione.map((key) => ({ key })),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      setSnack({
        severity: response?.data?.ok ? "success" : "warning",
        messaggio: `${response?.data?.appliedCount || 0} correzioni applicate`,
      });
      setOpenSnack(true);
      await analyzeRiconciliazioneAttestati();
      await fetchScadenzario();
      await fetchStoricoRiconciliazione();
    } catch (error) {
      setSnack({
        severity: "error",
        messaggio:
          error?.response?.data?.error || "Errore durante la riconciliazione",
      });
      setOpenSnack(true);
      setLoadingRiconciliazione(false);
    }
  };

  const fetchStoricoRiconciliazione = async () => {
    if (sessionStorage.getItem("Autorizzazione") !== "1") return;
    try {
      const response = await unsplash.get(
        "corsi/attestatiRiconciliazione/history",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      setStoricoRiconciliazione(response?.data?.data || []);
    } catch (_error) {
      setStoricoRiconciliazione([]);
    }
  };

  const restoreRiconciliazione = async (logId) => {
    if (
      !window.confirm("Ripristinare i dati precedenti a questa correzione?")
    ) {
      return;
    }
    setLoadingRiconciliazione(true);
    try {
      await unsplash.post(
        "corsi/attestatiRiconciliazione/restore",
        { logId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      setSnack({ severity: "success", messaggio: "Correzione ripristinata" });
      setOpenSnack(true);
      await fetchStoricoRiconciliazione();
      await fetchScadenzario();
      if (clienteRiconciliazione) await analyzeRiconciliazioneAttestati();
    } catch (error) {
      setSnack({
        severity: "error",
        messaggio: error?.response?.data?.error || "Ripristino non riuscito",
      });
      setOpenSnack(true);
    } finally {
      setLoadingRiconciliazione(false);
    }
  };

  useEffect(() => {
    // è un loop qui carico i dati
    fetchScadenzario();
  }, []);

  useEffect(() => {
    setFiltroDateInput(filtroDate);
  }, [filtroDate.dal, filtroDate.al]);

  useEffect(() => {
    const fetchCorsi = async () => {
      try {
        let responsecorsiCliente = await unsplash.get(
          "corsiCliente",
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }, //props.idAzienda}}
        );
        setCorsi(responsecorsiCliente.data.data);
        let responseCorsiH = await unsplash.get("corsi", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        });
        setCorsiImportOptions(responseCorsiH.data.data || []);
        let responseTipiCorso = await unsplash.get("anagraficaCorsi", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        });
        setTipiCorsoImportOptions(responseTipiCorso.data.data || []);
        setLoaded(false);
      } catch (error) {
        console.error(error.message);
      }
    };

    fetchCorsi();
  }, []);

  const handleChangeCorsiSel = (values) => {
    const nextCodes = (values || [])
      .map((v) => String(v?.Codice || "").trim())
      .filter(Boolean);
    setCorsiSel(nextCodes);
    filterData("", "", nextCodes);
  };

  const handleChangeData = (field) => (event) => {
    setFiltroDateInput((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleBlurData = (field) => () => {
    const nextRange = { ...filtroDate, [field]: filtroDateInput[field] };
    setMesiScadenza(0);
    setFiltroDate(nextRange);
    filterData("", "", corsiSel, nextRange);
  };

  const handleMesiScadenza = (_, value) => {
    setMesiScadenza(value);
    if (value === 0) return;

    const dal = filtroDateInput.dal || moment().format("YYYY-MM-DD");
    const nextRange = {
      dal,
      al: moment(dal, "YYYY-MM-DD").add(value, "months").format("YYYY-MM-DD"),
    };
    setFiltroDateInput(nextRange);
    setFiltroDate(nextRange);
    filterData("", "", corsiSel, nextRange);
  };

  const handleMostraTutto = (event) => {
    const checked = event.target.checked;
    setMostraTutto(checked);
    filterData("", "", corsiSel, filtroDate, checked);
  };

  // La lista lavoratori e nell'ordine di migliaia: si sceglie prima l'azienda
  // e si filtra, invece di scorrere un elenco unico.
  const aziendeAttestatoManuale = useMemo(() => {
    const byId = new Map();
    (lavoratoriAttestatoManuale || []).forEach((l) => {
      const id = Number(l?.Cliente || 0);
      if (!id || byId.has(id)) return;
      byId.set(id, {
        Cliente: id,
        RagioneSociale: String(l?.RagioneSociale || "").trim(),
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.RagioneSociale.localeCompare(b.RagioneSociale),
    );
  }, [lavoratoriAttestatoManuale]);

  const lavoratoriFiltratiAttestatoManuale = useMemo(() => {
    const idAzienda = Number(aziendaAttestatoManuale?.Cliente || 0);
    if (!idAzienda) return [];
    return (lavoratoriAttestatoManuale || [])
      .filter((l) => Number(l?.Cliente || 0) === idAzienda)
      .sort((a, b) =>
        `${a?.Cognome || ""} ${a?.Nome || ""}`
          .trim()
          .localeCompare(`${b?.Cognome || ""} ${b?.Nome || ""}`.trim()),
      );
  }, [lavoratoriAttestatoManuale, aziendaAttestatoManuale]);

  const resetAttestatoManualeState = () => {
    setAziendaAttestatoManuale(null);
    setLavoratoreAttestatoManuale(null);
    setRigheAttestatoManuale([buildRigaAttestatoManuale(1)]);
    setProgressivoRigaAttestatoManuale(1);
    setEsitiAttestatoManuale({});
  };

  const handleOpenAttestatoManuale = async () => {
    resetAttestatoManualeState();
    setOpenAttestatoManuale(true);
    if (lavoratoriAttestatoManuale.length > 0) return;
    try {
      setLoadingLavoratoriAttestatoManuale(true);
      const response = await unsplash.get("Lavoratori", {
        params: { lavoratoriSicurezza: "SI" },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
      });
      setLavoratoriAttestatoManuale(response?.data?.data || []);
    } catch (error) {
      console.error(error.message);
      setSnack({
        severity: "error",
        messaggio: "Errore nel caricamento dei lavoratori",
      });
      setOpenSnack(true);
    } finally {
      setLoadingLavoratoriAttestatoManuale(false);
    }
  };

  const handleChangeRigaAttestatoManuale = (id, campo, valore) => {
    setRigheAttestatoManuale((righe) =>
      righe.map((riga) =>
        riga.id === id ? { ...riga, [campo]: valore } : riga,
      ),
    );
    // L'errore riguardava il valore precedente: appena si corregge, sparisce.
    setEsitiAttestatoManuale((esiti) => {
      if (!esiti[id]) return esiti;
      const next = { ...esiti };
      delete next[id];
      return next;
    });
  };

  const handleAddRigaAttestatoManuale = () => {
    const nuovoId = progressivoRigaAttestatoManuale + 1;
    setProgressivoRigaAttestatoManuale(nuovoId);
    setRigheAttestatoManuale((righe) => [
      ...righe,
      buildRigaAttestatoManuale(nuovoId),
    ]);
  };

  const handleRemoveRigaAttestatoManuale = (id) => {
    setRigheAttestatoManuale((righe) =>
      righe.length > 1 ? righe.filter((riga) => riga.id !== id) : righe,
    );
  };

  const handleSaveAttestatoManuale = async () => {
    const idLavoratore = Number(lavoratoreAttestatoManuale?.ID || 0);
    if (!idLavoratore) {
      setSnack({ severity: "error", messaggio: "Seleziona un lavoratore" });
      setOpenSnack(true);
      return;
    }
    const oggi = moment().startOf("day");
    const righeValide = righeAttestatoManuale.filter(
      (riga) => riga.tipoCorso || riga.data,
    );
    if (righeValide.length === 0) {
      setSnack({
        severity: "error",
        messaggio: "Inserisci almeno una formazione con tipo corso e data",
      });
      setOpenSnack(true);
      return;
    }
    const righeIncomplete = righeValide.some(
      (riga) =>
        !riga.tipoCorso?.Codice ||
        !moment(riga.data, "YYYY-MM-DD", true).isValid(),
    );
    if (righeIncomplete) {
      setSnack({
        severity: "error",
        messaggio: "Compila tipo corso e data per ogni riga",
      });
      setOpenSnack(true);
      return;
    }
    // Solo la data corso non puo essere futura: una scadenza futura e normale.
    const righeFuture = righeValide.some(
      (riga) =>
        riga.modalita !== "scadenza" &&
        moment(riga.data, "YYYY-MM-DD", true).isAfter(oggi, "day"),
    );
    if (righeFuture) {
      setSnack({
        severity: "error",
        messaggio:
          'La data del corso non può essere futura: se conosci solo la scadenza, cambia "Inserisco" in "Data scadenza"',
      });
      setOpenSnack(true);
      return;
    }
    const righePermanentiConScadenza = righeValide.filter(
      (riga) =>
        riga.modalita === "scadenza" &&
        anniValiditaAttestatoManuale(riga.tipoCorso) === 0,
    );
    if (righePermanentiConScadenza.length > 0) {
      setSnack({
        severity: "error",
        messaggio: `${righePermanentiConScadenza
          .map((r) => r.tipoCorso.Codice)
          .join(
            ", ",
          )}: formazione permanente, non ha scadenza. Cambia "Inserisco" in "Data corso"`,
      });
      setOpenSnack(true);
      return;
    }

    setLoadingSaveAttestatoManuale(true);
    try {
      const response = await unsplash.post(
        "/corsi/attestatoManuale",
        {
          lavoratore: idLavoratore,
          attestati: righeValide.map((riga) => ({
            tipoCorsoCodice: riga.tipoCorso.Codice,
            aggiornamento: riga.aggiornamento,
            modalita: riga.modalita,
            data: riga.data,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      const importedCount = response?.data?.importedCount || 0;
      const skippedCount = response?.data?.skippedCount || 0;
      // I risultati tornano nello stesso ordine delle righe inviate: li rimappo
      // sulla riga di origine, cosi l'errore si legge dove va corretto.
      const results = response?.data?.results || [];
      const idsRegistrati = new Set();
      const esiti = {};
      righeValide.forEach((riga, indice) => {
        const esito = results[indice];
        if (!esito) return;
        if (esito.status === "ok") idsRegistrati.add(riga.id);
        else esiti[riga.id] = esito.message || "Riga non registrata";
      });

      if (importedCount > 0) await fetchScadenzario();

      const etichetta = (n) =>
        n === 1 ? "1 formazione registrata" : `${n} formazioni registrate`;

      if (skippedCount === 0) {
        setSnack({ severity: "success", messaggio: etichetta(importedCount) });
        setOpenSnack(true);
        setOpenAttestatoManuale(false);
        resetAttestatoManualeState();
        return;
      }

      // Le righe scartate restano a video con il motivo: chiudere il dialog
      // le farebbe perdere, ed e proprio quando serve poterle correggere.
      setEsitiAttestatoManuale(esiti);
      setRigheAttestatoManuale((righe) =>
        righe.filter((riga) => !idsRegistrati.has(riga.id)),
      );
      setSnack({
        severity: importedCount > 0 ? "warning" : "error",
        messaggio:
          importedCount > 0
            ? `${etichetta(importedCount)}, ${skippedCount} da correggere qui sotto`
            : `Nessuna formazione registrata: ${skippedCount} da correggere qui sotto`,
      });
      setOpenSnack(true);
    } catch (error) {
      console.error(error.message);
      setSnack({
        severity: "error",
        messaggio:
          error?.response?.data?.error ||
          "Errore durante la registrazione della formazione",
      });
      setOpenSnack(true);
    } finally {
      setLoadingSaveAttestatoManuale(false);
    }
  };

  const resetImportAttestatiMassiviState = () => {
    setFilesAttestatiMassivi([]);
    setAnalisiAttestatiMassivi([]);
    setJobAttestatiMassivi("");
    setDateChoicesAttestatiMassivi({});
    setPageDirectivesAttestatiMassivi({});
    setSuggestedCoursesAttestatiMassivi([]);
    setTipiCorsoImportFilteredOptions([]);
    setSuggestedCourseIdAttestatiMassivi("");
    setCorsoImportSel([]);
    setTipoCorsoImportSel("");
    setConfirmAnomaliesAttestatiMassivi([]);
  };

  const handleAnalyzeAttestatiMassivi = async () => {
    if (!filesAttestatiMassivi.length) {
      setSnack({
        severity: "error",
        messaggio: "Seleziona almeno un PDF da analizzare",
      });
      setOpenSnack(true);
      return;
    }
    setConfirmAnomaliesAttestatiMassivi([]);
    setLoadingAnalyzeAttestatiMassivi(true);
    try {
      const formData = new FormData();
      filesAttestatiMassivi.forEach((f) => formData.append("file", f));
      const response = await unsplash.post(
        "/corsi/attestatiMassivi/analyze",
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      const files = response?.data?.files || [];
      const suggestedCoursesRaw = response?.data?.suggestedCourses || [];
      const suggestedCourses = dedupeCourseOptions(suggestedCoursesRaw);
      const suggestedCourseIds = response?.data?.suggestedCourseIds || [];
      const suggestedRoleOptions = response?.data?.suggestedRoleOptions || [];
      const suggestedCourseId = response?.data?.suggestedCourseId || "";
      setAnalisiAttestatiMassivi(files);
      setJobAttestatiMassivi(response?.data?.jobId || "");
      setSuggestedCoursesAttestatiMassivi(suggestedCourses);
      setTipiCorsoImportFilteredOptions(suggestedRoleOptions);
      setSuggestedCourseIdAttestatiMassivi(suggestedCourseId);
      const allowedIds = new Set(suggestedCourses.map((c) => String(c.ID)));
      const suggestedCourseIdsFiltered = (
        Array.isArray(suggestedCourseIds) ? suggestedCourseIds : []
      ).filter((id) => allowedIds.has(String(id)));
      setCorsoImportSel(
        suggestedCourseIdsFiltered.length > 0
          ? suggestedCourseIdsFiltered
          : suggestedCourseId && allowedIds.has(String(suggestedCourseId))
            ? [suggestedCourseId]
            : [],
      );
      const suggestedTop =
        suggestedCourses.length > 0 ? suggestedCourses[0] : null;
      setTipoCorsoImportSel(
        suggestedTop?.TipoCodice || suggestedRoleOptions?.[0]?.Codice || "",
      );
      const initialChoices = {};
      const sourceCounter = {};
      files.forEach((f) => {
        if (f.sourceOriginalName) {
          sourceCounter[f.sourceOriginalName] =
            (sourceCounter[f.sourceOriginalName] || 0) + 1;
        }
      });
      const initialDirectives = {};
      files.forEach((f) => {
        if (f.selectedDate) {
          initialChoices[f.fileId] = f.selectedDate;
        }
        const hasAnyCf =
          Array.isArray(f.cfCandidates) && f.cfCandidates.length > 0;
        const hasWorker = Number(f?.selectedLavoratore?.Lavoratore || 0) > 0;
        const hasAnyDate =
          Boolean(String(f.selectedDate || "").trim()) ||
          (Array.isArray(f.dateCandidates) && f.dateCandidates.length > 0);
        const hasAnyRole =
          Array.isArray(f.rolesFound) && f.rolesFound.length > 0;
        const hasAnyCourse =
          Array.isArray(f.coursesFound) && f.coursesFound.length > 0;
        const hasNoEvidence =
          !hasAnyCf &&
          !hasWorker &&
          !hasAnyDate &&
          !hasAnyRole &&
          !hasAnyCourse;
        const hasMultiSource =
          !!f.sourceOriginalName &&
          (sourceCounter[f.sourceOriginalName] || 0) > 1;
        initialDirectives[f.fileId] = {
          include: !f.languageFilteredOut && !hasNoEvidence,
          aggregate: hasMultiSource,
        };
      });
      setDateChoicesAttestatiMassivi(initialChoices);
      setPageDirectivesAttestatiMassivi(initialDirectives);
      setSnack({
        severity: "info",
        messaggio:
          suggestedCourses.length > 0
            ? `Analisi completata: ${response?.data?.summary?.aggiornamentoFilesDetected || 0} attestati aggiornamento rilevati, corso consigliato selezionato`
            : `Analisi completata: nessun corso consigliato, selezionalo manualmente`,
      });
      setOpenSnack(true);
    } catch (_e) {
      setSnack({
        severity: "error",
        messaggio: "Errore durante l'analisi attestati massivi",
      });
      setOpenSnack(true);
    } finally {
      setLoadingAnalyzeAttestatiMassivi(false);
    }
  };

  const handleConfirmImportAttestatiMassivi = async () => {
    if (!jobAttestatiMassivi) {
      setSnack({
        severity: "error",
        messaggio: "Analizza prima i file",
      });
      setOpenSnack(true);
      return;
    }
    const includedFiles = analisiAttestatiMassivi.filter((f) => {
      const d = pageDirectivesAttestatiMassivi[f.fileId] || {};
      return d.include !== false;
    });
    const missingDateChoice = includedFiles.some((f) => {
      const d = pageDirectivesAttestatiMassivi[f.fileId] || {};
      const aggregate = !!d.aggregate;
      return (
        !aggregate &&
        f.requiresDateChoice &&
        !dateChoicesAttestatiMassivi[f.fileId]
      );
    });
    const aggregateGroups = buildAggregateGroupsForImport(
      analisiAttestatiMassivi,
      pageDirectivesAttestatiMassivi,
    );
    const missingDateChoiceAggregate = aggregateGroups.some((group) => {
      const needsChoice = group.items.some((f) => f.requiresDateChoice);
      if (!needsChoice) return false;
      return !group.items.some((f) => {
        const raw = dateChoicesAttestatiMassivi[f.fileId];
        return Boolean(String(raw || "").trim());
      });
    });
    if ((corsoImportSel || []).length === 0 && !tipoCorsoImportSel) {
      setSnack({
        severity: "error",
        messaggio:
          "Seleziona un corso consigliato oppure un tipo corso per import solo scadenzario",
      });
      setOpenSnack(true);
      return;
    }
    if (missingDateChoice || missingDateChoiceAggregate) {
      setSnack({
        severity: "error",
        messaggio:
          "Per i file con piu date seleziona la data da usare come data corso (nei gruppi aggregati basta una sola data)",
      });
      setOpenSnack(true);
      return;
    }
    setLoadingConfirmAttestatiMassivi(true);
    try {
      const response = await unsplash.post(
        "/corsi/attestatiMassivi/confirm",
        {
          jobId: jobAttestatiMassivi,
          corsoId:
            Array.isArray(corsoImportSel) && corsoImportSel.length > 0
              ? corsoImportSel[0]
              : 0,
          corsoIds: corsoImportSel,
          tipoCorsoCodice: tipoCorsoImportSel,
          dateChoices: dateChoicesAttestatiMassivi,
          pageDirectives: pageDirectivesAttestatiMassivi,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        },
      );
      const importedCount = response?.data?.importedCount || 0;
      const skippedCount = response?.data?.skippedCount || 0;
      const anomalies = response?.data?.anomalies || [];
      setConfirmAnomaliesAttestatiMassivi(anomalies);
      const anomalyPreview = anomalies
        .slice(0, 2)
        .map((a) => a.message)
        .join(" | ");
      const hasSkipped = skippedCount > 0;
      const severity = hasSkipped
        ? "warning"
        : importedCount > 0
          ? "success"
          : "info";
      const outcomeLabel = hasSkipped ? "Import parziale" : "Import completato";
      setSnack({
        severity,
        messaggio:
          hasSkipped && anomalyPreview
            ? `${outcomeLabel}: ${importedCount} importati, ${skippedCount} anomalie. ${anomalyPreview}`
            : `${outcomeLabel}: ${importedCount} importati, ${skippedCount} anomalie`,
      });
      setOpenSnack(true);
      if (skippedCount === 0) {
        setOpenImportAttestatiMassivi(false);
        setFilesAttestatiMassivi([]);
        setAnalisiAttestatiMassivi([]);
        setJobAttestatiMassivi("");
        setDateChoicesAttestatiMassivi({});
        setPageDirectivesAttestatiMassivi({});
        setSuggestedCoursesAttestatiMassivi([]);
        setTipiCorsoImportFilteredOptions([]);
        setSuggestedCourseIdAttestatiMassivi("");
        setCorsoImportSel([]);
        setTipoCorsoImportSel("");
        setConfirmAnomaliesAttestatiMassivi([]);
      }
    } catch (_e) {
      setSnack({
        severity: "error",
        messaggio: "Errore durante l'importazione attestati massivi",
      });
      setOpenSnack(true);
    } finally {
      setLoadingConfirmAttestatiMassivi(false);
    }
  };

  const importHeadCellStyle = {
    textAlign: "left",
    fontWeight: 700,
    padding: "10px 8px",
    borderBottom: "1px solid #d9d9d9",
    verticalAlign: "top",
  };
  const importCellStyle = {
    padding: "10px 8px",
    borderBottom: "1px solid #ececec",
    verticalAlign: "top",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.35,
  };
  const aggregateGroupsForUi = buildAggregateGroupsForImport(
    analisiAttestatiMassivi,
    pageDirectivesAttestatiMassivi,
  );
  const aggregateGroupMetaByFileId = aggregateGroupsForUi.reduce(
    (acc, group) => {
      (group.items || []).forEach((item, idx) => {
        acc[item.fileId] = {
          groupKey: group.key,
          index: idx,
        };
      });
      return acc;
    },
    {},
  );
  const hasSuggestedCourses = suggestedCoursesAttestatiMassivi.length > 0;

  return (
    <>
      <GlobalSnackbar
        severity={snack.severity}
        messaggio={snack.messaggio}
        openSnack={openSnack}
        handleCloseSnack={() => setOpenSnack(false)}
      />
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} lg={3}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={corsi || []}
                  value={(corsi || []).filter((c) =>
                    (corsiSel || []).includes(String(c?.Codice || "")),
                  )}
                  onChange={(_, values) => handleChangeCorsiSel(values)}
                  disabled={loading}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.Codice || "") === String(value?.Codice || "")
                  }
                  getOptionLabel={(option) => {
                    const codice = String(option?.Codice || "").trim();
                    const descrizione = String(
                      option?.Descrizione || "",
                    ).trim();
                    return descrizione ? `${codice} - ${descrizione}` : codice;
                  }}
                  filterOptions={(options, state) => {
                    const q = String(state.inputValue || "")
                      .toLowerCase()
                      .trim();
                    if (!q) return options;
                    return options.filter((o) => {
                      const codice = String(o?.Codice || "").toLowerCase();
                      const descrizione = String(
                        o?.Descrizione || "",
                      ).toLowerCase();
                      return codice.includes(q) || descrizione.includes(q);
                    });
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        sx={{ mr: 1 }}
                      />
                      {String(option?.Codice || "").trim()}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Corsi"
                      placeholder="Cerca codice o descrizione"
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} lg={9}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", lg: "space-between" },
                    flexWrap: { xs: "wrap", lg: "nowrap" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "100%", sm: 360, lg: "auto" },
                      minWidth: { lg: 300 },
                      flex: { lg: "1 1 380px" },
                      px: 1,
                      pt: 0.5,
                    }}
                  >
                    <Box sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>
                      {mesiScadenza
                        ? `In scadenza entro: ${mesiScadenza} mesi`
                        : "Intervallo personalizzato"}
                    </Box>
                    <Slider
                      value={mesiScadenza}
                      onChange={handleMesiScadenza}
                      disabled={loading || mostraTutto}
                      step={1}
                      min={0}
                      max={12}
                      sx={{
                        // Le etichette estreme sono centrate sui capi della traccia
                        // e sborderebbero sui campi accanto: le ancoro ai bordi.
                        '& .MuiSlider-markLabel[data-index="0"]': {
                          transform: "translateX(0)",
                        },
                        '& .MuiSlider-markLabel[data-index="4"]': {
                          transform: "translateX(-100%)",
                        },
                      }}
                      marks={[
                        { value: 0, label: "Personalizzato" },
                        { value: 3, label: "3" },
                        { value: 6, label: "6" },
                        { value: 9, label: "9" },
                        { value: 12, label: "12 mesi" },
                      ]}
                      aria-label="Scadenze entro mesi"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <TextField
                      label="Dal"
                      type="date"
                      size="small"
                      value={filtroDateInput.dal}
                      onChange={handleChangeData("dal")}
                      onBlur={handleBlurData("dal")}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading || mostraTutto}
                      sx={{ width: 170 }}
                    />
                    <TextField
                      label="Al"
                      type="date"
                      size="small"
                      value={filtroDateInput.al}
                      onChange={handleChangeData("al")}
                      onBlur={handleBlurData("al")}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading || mostraTutto}
                      sx={{ width: 170 }}
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", lg: "flex-end" },
                }}
              >
                <Button
                  variant="contained"
                  className="ButtonMiller"
                  startIcon={<Add />}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0, minHeight: 40 }}
                  onClick={handleOpenAttestatoManuale}
                >
                  AGGIUNGI FORMAZIONE
                </Button>
                <Button
                  variant="contained"
                  className="ButtonMiller"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0, minHeight: 40 }}
                  onClick={() => {
                    resetImportAttestatiMassiviState();
                    setOpenImportAttestatiMassivi(true);
                  }}
                >
                  IMPORTA ATTESTATI MASSIVI
                </Button>
                <Button
                  variant="outlined"
                  className="ButtonMiller"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0, minHeight: 40 }}
                  onClick={() => {
                    setRigheRiconciliazione([]);
                    setSelezioneRiconciliazione([]);
                    setLavoratoreRiconciliazione(null);
                    setMostraCoerentiRiconciliazione(false);
                    fetchOpzioniRiconciliazione();
                    fetchStoricoRiconciliazione();
                    setOpenRiconciliazioneAttestati(true);
                  }}
                >
                  VERIFICA DATE ATTESTATI
                </Button>
              </Grid>
            </Grid>
            <PopUpCorsi lavoratore={lavoratore} />
            <Box
              sx={{
                height: { xs: "75vh", md: "60vh", xl: "75vh" },
                width: 1,
              }}
            >
              <DataGridPro
                apiRef={apiRef}
                sx={{ fontSize: 12 }}
                components={{
                  Toolbar: CustomToolbar,
                  Footer: FooterWithExport,
                }}
                slots={{
                  loadingOverlay: LinearProgress,
                }}
                loading={loading}
                localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
                rows={scadenzeCorsi}
                initialState={{
                  pinnedColumns: { left: ["Actions", "DataTermine"] },
                }}
                columns={columns}
                getRowId={(row) => row.Lavoratore}
                pageSize={100}
                disableSelectionOnClick
              />
            </Box>
            <Dialog
              open={openAttestatoManuale}
              onClose={() => {
                if (loadingSaveAttestatoManuale) return;
                setOpenAttestatoManuale(false);
                resetAttestatoManualeState();
              }}
              fullWidth
              maxWidth="lg"
              TransitionComponent={Transition}
            >
              <DialogTitle>Aggiungi formazione</DialogTitle>
              <DialogContent dividers sx={{ overflowX: "hidden" }}>
                <DialogContentText sx={{ mb: 2 }}>
                  Registra nello scadenzario una formazione già svolta, senza
                  bisogno dell&apos;attestato: il documento può arrivare dopo, o
                  mai. Per ogni riga puoi indicare la data del corso oppure, se
                  conosci solo quella, la data di scadenza.
                </DialogContentText>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      size="small"
                      options={aziendeAttestatoManuale}
                      loading={loadingLavoratoriAttestatoManuale}
                      value={aziendaAttestatoManuale}
                      onChange={(_e, value) => {
                        setAziendaAttestatoManuale(value);
                        setLavoratoreAttestatoManuale(null);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        Number(option?.Cliente || 0) ===
                        Number(value?.Cliente || 0)
                      }
                      getOptionLabel={(o) => o?.RagioneSociale || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Azienda"
                          placeholder="Cerca azienda"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      size="small"
                      options={lavoratoriFiltratiAttestatoManuale}
                      disabled={!aziendaAttestatoManuale}
                      value={lavoratoreAttestatoManuale}
                      onChange={(_e, value) =>
                        setLavoratoreAttestatoManuale(value)
                      }
                      isOptionEqualToValue={(option, value) =>
                        Number(option?.ID || 0) === Number(value?.ID || 0)
                      }
                      getOptionLabel={labelLavoratoreAttestatoManuale}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Lavoratore"
                          placeholder={
                            aziendaAttestatoManuale
                              ? "Cerca per cognome o CF"
                              : "Seleziona prima l'azienda"
                          }
                        />
                      )}
                    />
                  </Grid>
                  {righeAttestatoManuale.map((riga) => (
                    <Grid item xs={12} key={riga.id}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                        }}
                      >
                        <Box sx={{ flex: "2 1 240px", minWidth: 0 }}>
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={tipiCorsoImportOptions}
                            value={riga.tipoCorso}
                            onChange={(_e, value) =>
                              handleChangeRigaAttestatoManuale(
                                riga.id,
                                "tipoCorso",
                                value,
                              )
                            }
                            isOptionEqualToValue={(option, value) =>
                              String(option?.Codice || "") ===
                              String(value?.Codice || "")
                            }
                            getOptionLabel={labelTipoCorsoAttestatoManuale}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Tipo corso"
                                placeholder="Cerca codice o descrizione"
                              />
                            )}
                          />
                        </Box>
                        <Box sx={{ flex: "1 1 150px", minWidth: 0 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id={`tipologia-attestato-${riga.id}`}>
                              Tipologia
                            </InputLabel>
                            <Select
                              labelId={`tipologia-attestato-${riga.id}`}
                              label="Tipologia"
                              value={riga.aggiornamento ? "1" : "0"}
                              onChange={(e) =>
                                handleChangeRigaAttestatoManuale(
                                  riga.id,
                                  "aggiornamento",
                                  e.target.value === "1",
                                )
                              }
                            >
                              <MenuItem value="0">Corso base</MenuItem>
                              <MenuItem value="1">Aggiornamento</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ flex: "1 1 150px", minWidth: 0 }}>
                          <FormControl size="small" fullWidth>
                            <InputLabel id={`modalita-attestato-${riga.id}`}>
                              Inserisco
                            </InputLabel>
                            <Select
                              labelId={`modalita-attestato-${riga.id}`}
                              label="Inserisco"
                              value={riga.modalita}
                              onChange={(e) =>
                                handleChangeRigaAttestatoManuale(
                                  riga.id,
                                  "modalita",
                                  e.target.value,
                                )
                              }
                            >
                              <MenuItem value="corso">Data corso</MenuItem>
                              <MenuItem value="scadenza">
                                Data scadenza
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ flex: "1 1 155px", minWidth: 0 }}>
                          <TextField
                            fullWidth
                            label={
                              riga.modalita === "scadenza"
                                ? "Data scadenza"
                                : "Data corso"
                            }
                            type="date"
                            size="small"
                            value={riga.data}
                            onChange={(e) =>
                              handleChangeRigaAttestatoManuale(
                                riga.id,
                                "data",
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={
                              riga.modalita === "scadenza"
                                ? {}
                                : { max: moment().format("YYYY-MM-DD") }
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            flex: "1 1 190px",
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            label={anteprimaAttestatoManuale(riga) || "-"}
                            sx={{ maxWidth: "100%" }}
                          />
                          <Tooltip title="Rimuovi riga">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRemoveRigaAttestatoManuale(riga.id)
                                }
                                disabled={righeAttestatoManuale.length === 1}
                              >
                                <Delete />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                      {esitiAttestatoManuale[riga.id] && (
                        <Box
                          sx={{
                            color: "error.main",
                            fontSize: 12,
                            mt: 0.5,
                            pl: 0.5,
                          }}
                        >
                          {esitiAttestatoManuale[riga.id]}
                        </Box>
                      )}
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      startIcon={<Add />}
                      onClick={handleAddRigaAttestatoManuale}
                    >
                      Aggiungi un&apos;altra formazione
                    </Button>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mx: 2,
                  py: 1.5,
                }}
              >
                <Button
                  variant="contained"
                  className="ButtonMiller"
                  startIcon={<Close />}
                  onClick={() => {
                    setOpenAttestatoManuale(false);
                    resetAttestatoManualeState();
                  }}
                  disabled={loadingSaveAttestatoManuale}
                >
                  Chiudi
                </Button>
                <Button
                  variant="contained"
                  className="ButtonMiller"
                  onClick={handleSaveAttestatoManuale}
                  disabled={loadingSaveAttestatoManuale}
                >
                  Salva
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={openRiconciliazioneAttestati}
              onClose={() => {
                if (!loadingRiconciliazione) {
                  setOpenRiconciliazioneAttestati(false);
                }
              }}
              fullWidth
              maxWidth="lg"
            >
              <DialogTitle>Riconciliazione date attestati</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                  L'analisi non modifica alcun dato. Seleziona il cliente,
                  controlla le anomalie e applica soltanto le correzioni
                  confermate.
                </DialogContentText>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <Autocomplete
                    sx={{ minWidth: 360 }}
                    options={clientiRiconciliazione}
                    filterOptions={(options, state) => {
                      const query = normalizeCompanySearch(state.inputValue);
                      if (!query) return options;
                      return options.filter((option) =>
                        normalizeCompanySearch(option.label).includes(query),
                      );
                    }}
                    value={clienteRiconciliazione}
                    onChange={(_event, value) => {
                      setClienteRiconciliazione(value);
                      setLavoratoreRiconciliazione(null);
                      setLavoratoriRiconciliazioneApi([]);
                      if (value?.id) fetchOpzioniRiconciliazione(value.id);
                      setRigheRiconciliazione([]);
                      setSelezioneRiconciliazione([]);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Cliente" size="small" />
                    )}
                  />
                  <Autocomplete
                    sx={{ minWidth: 320 }}
                    options={lavoratoriRiconciliazione}
                    value={lavoratoreRiconciliazione}
                    disabled={!clienteRiconciliazione}
                    getOptionLabel={(option) =>
                      `${option.label}${option.cf ? ` (${option.cf})` : ""}`
                    }
                    onChange={(_event, value) => {
                      setLavoratoreRiconciliazione(value);
                      setRigheRiconciliazione([]);
                      setSelezioneRiconciliazione([]);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lavoratore (facoltativo)"
                        size="small"
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={analyzeRiconciliazioneAttestati}
                    disabled={!clienteRiconciliazione || loadingRiconciliazione}
                  >
                    Analizza
                  </Button>
                </Box>
                {loadingRiconciliazione && <LinearProgress sx={{ mb: 2 }} />}
                {righeRiconciliazione.length > 0 && (
                  <>
                    <FormControlLabel
                      sx={{ mb: 1 }}
                      control={
                        <Checkbox
                          checked={mostraCoerentiRiconciliazione}
                          onChange={(event) =>
                            setMostraCoerentiRiconciliazione(
                              event.target.checked,
                            )
                          }
                        />
                      }
                      label={`Mostra coerenti (${
                        righeRiconciliazione.filter(
                          (row) => row.status === "ok",
                        ).length
                      })`}
                    />
                    <Box sx={{ maxHeight: 480, overflow: "auto" }}>
                      {righeRiconciliazione
                        .filter(
                          (row) =>
                            mostraCoerentiRiconciliazione ||
                            row.status !== "ok",
                        )
                        .map((row) => (
                          <Card key={row.key} variant="outlined" sx={{ mb: 1 }}>
                            <CardContent
                              sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Checkbox
                                  checked={selezioneRiconciliazione.includes(
                                    row.key,
                                  )}
                                  disabled={!row.canApply}
                                  onChange={(event) =>
                                    setSelezioneRiconciliazione((previous) =>
                                      event.target.checked
                                        ? [...previous, row.key]
                                        : previous.filter(
                                            (key) => key !== row.key,
                                          ),
                                    )
                                  }
                                />
                                <Box sx={{ flex: 1 }}>
                                  <b>{row.lavoratore}</b> ({row.cf}) -{" "}
                                  {row.tipo} (
                                  {row.aggiornamento ? "Aggiornamento" : "Base"}
                                  )
                                  <Box sx={{ fontSize: 13, mt: 0.5 }}>
                                    Koala:{" "}
                                    {moment(row.dataKoala).format("DD/MM/YYYY")}{" "}
                                    {row.dataKoalaFine &&
                                    row.dataKoalaFine !== row.dataKoala
                                      ? `- ${moment(row.dataKoalaFine).format(
                                          "DD/MM/YYYY",
                                        )} `
                                      : ""}
                                    | Attestato:{" "}
                                    {moment(row.dataAttestato).format(
                                      "DD/MM/YYYY",
                                    )}
                                  </Box>
                                  {row.action === "classification" && (
                                    <Box
                                      sx={{
                                        color: "warning.main",
                                        fontSize: 12,
                                      }}
                                    >
                                      Classificazione suggerita: Aggiornamento
                                    </Box>
                                  )}
                                  {row.blockedReason && (
                                    <Box
                                      sx={{
                                        color: "warning.main",
                                        fontSize: 12,
                                      }}
                                    >
                                      {row.blockedReason}
                                    </Box>
                                  )}
                                  {row.impact && row.status !== "ok" && (
                                    <Box sx={{ fontSize: 12, mt: 0.5 }}>
                                      <b>Impatto:</b> {row.impact}
                                    </Box>
                                  )}
                                  {row.attachmentCount > 1 && (
                                    <Box sx={{ fontSize: 12 }}>
                                      {row.attachmentCount} attestati gestiti
                                      insieme
                                    </Box>
                                  )}
                                </Box>
                                <Chip
                                  size="small"
                                  color={
                                    row.status === "mismatch"
                                      ? "warning"
                                      : "success"
                                  }
                                  label={
                                    row.status === "mismatch"
                                      ? "Da verificare"
                                      : "Coerente"
                                  }
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                    </Box>
                  </>
                )}
                {!loadingRiconciliazione &&
                  clienteRiconciliazione &&
                  righeRiconciliazione.length === 0 && (
                    <DialogContentText>
                      Nessuna anomalia rilevata o analisi non ancora eseguita.
                    </DialogContentText>
                  )}
                {storicoRiconciliazione.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <b>Ultime correzioni</b>
                    {storicoRiconciliazione.slice(0, 10).map((item) => (
                      <Box
                        key={item.ID}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <Box sx={{ flex: 1, fontSize: 12 }}>
                          <Box sx={{ fontWeight: 600 }}>
                            {[item.Nome, item.Cognome]
                              .filter(Boolean)
                              .join(" ") || `Lavoratore #${item.Lavoratore}`}
                            {item.RagioneSociale
                              ? ` — ${item.RagioneSociale}`
                              : ""}
                          </Box>
                          <Box>
                            #{item.ID} - {item.Tipo} - {item.Azione} -{" "}
                            {moment(item.DataOperazione).format(
                              "DD/MM/YYYY HH:mm",
                            )}
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => restoreRiconciliazione(item.ID)}
                          disabled={
                            !!item.Ripristinato || loadingRiconciliazione
                          }
                        >
                          {item.Ripristinato ? "Ripristinata" : "Ripristina"}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOpenRiconciliazioneAttestati(false)}
                  disabled={loadingRiconciliazione}
                >
                  Chiudi
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={applyRiconciliazioneAttestati}
                  disabled={
                    loadingRiconciliazione ||
                    !applyRiconciliazioneEnabled ||
                    !canManageRiconciliazione ||
                    selezioneRiconciliazione.length === 0
                  }
                >
                  Applica correzioni selezionate
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={openImportAttestatiMassivi}
              onClose={() => {
                if (
                  loadingAnalyzeAttestatiMassivi ||
                  loadingConfirmAttestatiMassivi
                )
                  return;
                setOpenImportAttestatiMassivi(false);
                resetImportAttestatiMassiviState();
              }}
              fullWidth
              maxWidth="lg"
              PaperProps={{
                sx: {
                  width: "min(1400px, 98vw)",
                  maxWidth: "98vw",
                  maxHeight: "94vh",
                  display: "flex",
                },
              }}
              TransitionComponent={Transition}
            >
              <DialogTitle>Importazione Massiva Attestati</DialogTitle>
              <DialogContent dividers sx={{ overflowX: "hidden" }}>
                <DialogContentText sx={{ mb: 2 }}>
                  Carica PDF attestati. Il sistema rileva CF e date, segnala
                  anomalie e poi ti propone il corso consigliato in base a
                  quanto trovato nei file. Se il PDF e una scansione immagine,
                  prova OCR quando disponibile. Puoi escludere pagine o
                  aggregare pagine dello stesso PDF prima della conferma.
                </DialogContentText>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      multiple
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []);
                        resetImportAttestatiMassiviState();
                        setFilesAttestatiMassivi(selected);
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    {analisiAttestatiMassivi.length === 0 && (
                      <Button
                        variant="contained"
                        className="ButtonMiller"
                        onClick={handleAnalyzeAttestatiMassivi}
                        disabled={loadingAnalyzeAttestatiMassivi}
                      >
                        Analizza file
                      </Button>
                    )}
                    {analisiAttestatiMassivi.length > 0 && (
                      <Button
                        variant="contained"
                        className="ButtonMiller"
                        onClick={handleConfirmImportAttestatiMassivi}
                        disabled={loadingConfirmAttestatiMassivi}
                      >
                        Conferma import
                      </Button>
                    )}
                  </Grid>
                  {(loadingAnalyzeAttestatiMassivi ||
                    loadingConfirmAttestatiMassivi) && (
                    <Grid item xs={12}>
                      <LinearProgress />
                    </Grid>
                  )}
                  {analisiAttestatiMassivi.length > 0 && (
                    <Grid item xs={12} md={8}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="corso-import-label">
                          Corso consigliato
                        </InputLabel>
                        <Select
                          labelId="corso-import-label"
                          multiple
                          value={corsoImportSel}
                          label="Corso consigliato"
                          disabled={!hasSuggestedCourses}
                          onChange={(e) => {
                            let nextCorsoIds = Array.isArray(e.target.value)
                              ? e.target.value
                              : [e.target.value];
                            if (nextCorsoIds.some((v) => String(v) === "")) {
                              nextCorsoIds = [];
                            }
                            setCorsoImportSel(nextCorsoIds);
                            const options =
                              suggestedCoursesAttestatiMassivi.length > 0
                                ? suggestedCoursesAttestatiMassivi
                                : corsiImportOptions;
                            const selected = options.find((c) =>
                              nextCorsoIds.some(
                                (id) => String(c.ID) === String(id),
                              ),
                            );
                            if (selected?.TipoCodice) {
                              setTipoCorsoImportSel(selected.TipoCodice);
                            } else if (selected?.Tipo) {
                              setTipoCorsoImportSel(selected.Tipo);
                            }
                          }}
                          renderValue={(selectedIds) => {
                            const ids = Array.isArray(selectedIds)
                              ? selectedIds
                              : [];
                            if (ids.length === 0)
                              return "Nessun corso evento (import solo scadenzario)";
                            const options =
                              suggestedCoursesAttestatiMassivi.length > 0
                                ? suggestedCoursesAttestatiMassivi
                                : corsiImportOptions;
                            const labels = options
                              .filter((c) =>
                                ids.some((id) => String(id) === String(c.ID)),
                              )
                              .map(
                                (c) =>
                                  `${Number(c.Aggiornamento || 0) === 1 ? "[AGG]" : "[BASE]"} #${c.ID} ${c.Descrizione}`,
                              );
                            return labels.join(" | ");
                          }}
                          sx={{
                            "& .MuiSelect-select": {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        >
                          <MenuItem value="">
                            Nessun corso evento (import solo scadenzario)
                          </MenuItem>
                          {suggestedCoursesAttestatiMassivi.map((c) => (
                            <MenuItem key={c.ID} value={c.ID}>
                              <Checkbox
                                size="small"
                                checked={(corsoImportSel || []).some(
                                  (id) => String(id) === String(c.ID),
                                )}
                              />
                              {Number(c.Aggiornamento || 0) === 1
                                ? "[AGG] "
                                : "[BASE] "}
                              #{c.ID} {c.Descrizione}{" "}
                              {c.DataInizio
                                ? `(${moment(c.DataInizio).format("DD/MM/YYYY")})`
                                : ""}
                              {Number(c.WorkerMatch || 0) > 0
                                ? ` - match lavoratori: ${c.WorkerMatch}`
                                : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {analisiAttestatiMassivi.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        size="small"
                        options={
                          tipiCorsoImportFilteredOptions.length > 0
                            ? tipiCorsoImportFilteredOptions
                            : tipiCorsoImportOptions
                        }
                        value={
                          (tipiCorsoImportFilteredOptions.length > 0
                            ? tipiCorsoImportFilteredOptions
                            : tipiCorsoImportOptions
                          ).find(
                            (c) =>
                              String(c.Codice) === String(tipoCorsoImportSel),
                          ) || null
                        }
                        onChange={(_, value) =>
                          setTipoCorsoImportSel(value?.Codice || "")
                        }
                        isOptionEqualToValue={(option, value) =>
                          String(option?.Codice) === String(value?.Codice)
                        }
                        getOptionLabel={(option) => {
                          const codice = option?.Codice || "";
                          const descrizione = option?.Descrizione || "";
                          const match = Number(option?.WorkerMatch || 0);
                          const suffix =
                            match > 0 ? ` (${match} lavoratori)` : "";
                          return `${codice} - ${descrizione}${suffix}`.trim();
                        }}
                        filterOptions={(options, state) => {
                          const q = String(state.inputValue || "")
                            .toLowerCase()
                            .trim();
                          if (!q) return options;
                          return options.filter((o) => {
                            const codice = String(
                              o?.Codice || "",
                            ).toLowerCase();
                            const descrizione = String(
                              o?.Descrizione || "",
                            ).toLowerCase();
                            return (
                              codice.includes(q) || descrizione.includes(q)
                            );
                          });
                        }}
                        noOptionsText="Nessun tipo corso trovato"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Tipo corso (scadenzario)"
                            placeholder="Cerca per codice o descrizione"
                          />
                        )}
                      />
                    </Grid>
                  )}
                  {analisiAttestatiMassivi.length > 0 && (
                    <Grid item xs={12}>
                      {suggestedCourseIdAttestatiMassivi ? (
                        <DialogContentText sx={{ mb: 0 }}>
                          Corso selezionato automaticamente in base ai PDF
                          analizzati. Puoi comunque cambiarlo prima della
                          conferma.
                        </DialogContentText>
                      ) : (
                        <DialogContentText sx={{ mb: 0 }}>
                          Nessun corso consigliato con confidenza sufficiente:
                          seleziona manualmente il corso corretto.
                        </DialogContentText>
                      )}
                    </Grid>
                  )}
                  {analisiAttestatiMassivi.length > 0 && (
                    <Grid item xs={12}>
                      {confirmAnomaliesAttestatiMassivi.length > 0 && (
                        <Box
                          sx={{
                            mb: 1,
                            p: 1,
                            border: "1px solid #f2c9c9",
                            borderRadius: 1,
                            backgroundColor: "#fff7f7",
                          }}
                        >
                          <DialogContentText sx={{ mb: 0.5, fontWeight: 700 }}>
                            Anomalie conferma import
                          </DialogContentText>
                          <DialogContentText sx={{ mb: 0 }}>
                            {confirmAnomaliesAttestatiMassivi
                              .slice(0, 6)
                              .map((a) => a.message)
                              .join(" | ")}
                          </DialogContentText>
                        </Box>
                      )}
                      <Box
                        sx={{
                          maxHeight: "min(50vh, 520px)",
                          overflow: "auto",
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#fff",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                          cellPadding="8"
                        >
                          <colgroup>
                            <col style={{ width: "24%" }} />
                            <col style={{ width: "8%" }} />
                            <col style={{ width: "8%" }} />
                            <col style={{ width: "10%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "12%" }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th style={importHeadCellStyle}>File</th>
                              <th style={importHeadCellStyle}>Includi</th>
                              <th style={importHeadCellStyle}>Aggrega</th>
                              <th style={importHeadCellStyle}>
                                Tipo attestato
                              </th>
                              <th style={importHeadCellStyle}>CF</th>
                              <th style={importHeadCellStyle}>Lavoratore</th>
                              <th style={importHeadCellStyle}>Data corso</th>
                              <th style={importHeadCellStyle}>Anomalie</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analisiAttestatiMassivi.map((f) => (
                              <tr key={f.fileId}>
                                <td style={importCellStyle}>
                                  {f.sourceOriginalName && f.sourcePage
                                    ? `${f.sourceOriginalName} (pag. ${f.sourcePage})`
                                    : f.originalName}
                                </td>
                                <td style={importCellStyle}>
                                  {(() => {
                                    const rowDirective =
                                      pageDirectivesAttestatiMassivi[
                                        f.fileId
                                      ] || {};
                                    const include =
                                      rowDirective.include !== false;
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={include}
                                        onChange={(e) =>
                                          setPageDirectivesAttestatiMassivi(
                                            (prev) => ({
                                              ...prev,
                                              [f.fileId]: {
                                                ...(prev[f.fileId] || {}),
                                                include: e.target.checked,
                                                aggregate: e.target.checked
                                                  ? !!(prev[f.fileId] || {})
                                                      .aggregate
                                                  : false,
                                              },
                                            }),
                                          )
                                        }
                                      />
                                    );
                                  })()}
                                </td>
                                <td style={importCellStyle}>
                                  {(() => {
                                    const rowDirective =
                                      pageDirectivesAttestatiMassivi[
                                        f.fileId
                                      ] || {};
                                    const include =
                                      rowDirective.include !== false;
                                    if (!include) return "-";
                                    return (
                                      <Checkbox
                                        size="small"
                                        checked={
                                          !!(
                                            pageDirectivesAttestatiMassivi[
                                              f.fileId
                                            ] || {}
                                          ).aggregate
                                        }
                                        onChange={(e) =>
                                          setPageDirectivesAttestatiMassivi(
                                            (prev) => ({
                                              ...prev,
                                              [f.fileId]: {
                                                ...(prev[f.fileId] || {}),
                                                aggregate: e.target.checked,
                                              },
                                            }),
                                          )
                                        }
                                        disabled={
                                          !f.sourceOriginalName ||
                                          !Number.isFinite(Number(f.sourcePage))
                                        }
                                      />
                                    );
                                  })()}
                                </td>
                                <td style={importCellStyle}>
                                  {f.attestatoAggiornamento
                                    ? "Aggiornamento"
                                    : "Base"}
                                </td>
                                <td style={importCellStyle}>
                                  {(f.cfCandidates || []).join(", ") || "-"}
                                </td>
                                <td style={importCellStyle}>
                                  {f.selectedLavoratore
                                    ? `${f.selectedLavoratore.Cognome || ""} ${
                                        f.selectedLavoratore.Nome || ""
                                      }`.trim()
                                    : "-"}
                                </td>
                                <td style={importCellStyle}>
                                  {(() => {
                                    const rowDirective =
                                      pageDirectivesAttestatiMassivi[
                                        f.fileId
                                      ] || {};
                                    const include =
                                      rowDirective.include !== false;
                                    const aggregate = !!rowDirective.aggregate;
                                    const groupMeta =
                                      aggregateGroupMetaByFileId[f.fileId];
                                    const isAggregateFollower =
                                      include &&
                                      aggregate &&
                                      !!groupMeta &&
                                      groupMeta.index > 0;

                                    if (!include) {
                                      return "-";
                                    }

                                    if (isAggregateFollower) {
                                      return "-";
                                    }

                                    if (f.requiresDateChoice) {
                                      return (
                                        <FormControl
                                          size="small"
                                          sx={{ minWidth: 140, width: "100%" }}
                                        >
                                          <Select
                                            value={
                                              dateChoicesAttestatiMassivi[
                                                f.fileId
                                              ] || ""
                                            }
                                            onChange={(e) =>
                                              setDateChoicesAttestatiMassivi(
                                                (prev) => ({
                                                  ...prev,
                                                  [f.fileId]: e.target.value,
                                                }),
                                              )
                                            }
                                          >
                                            {(f.dateCandidates || []).map(
                                              (d) => (
                                                <MenuItem key={d} value={d}>
                                                  {moment(d).format(
                                                    "DD/MM/YYYY",
                                                  )}
                                                </MenuItem>
                                              ),
                                            )}
                                          </Select>
                                        </FormControl>
                                      );
                                    }
                                    if (f.selectedDate) {
                                      return moment(f.selectedDate).format(
                                        "DD/MM/YYYY",
                                      );
                                    }
                                    return "-";
                                  })()}
                                </td>
                                <td style={importCellStyle}>
                                  {(() => {
                                    const isAggregated = !!(
                                      pageDirectivesAttestatiMassivi[
                                        f.fileId
                                      ] || {}
                                    ).aggregate;
                                    if (isAggregated) {
                                      return "Nessuna";
                                    }
                                    return (f.anomalies || []).length > 0
                                      ? f.anomalies
                                          .map((a) => a.message)
                                          .join(" | ")
                                      : "Nessuna";
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mx: 2,
                  py: 1.5,
                  position: "sticky",
                  bottom: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                <Button
                  variant="contained"
                  className="ButtonMiller"
                  startIcon={<Close />}
                  onClick={() => setOpenImportAttestatiMassivi(false)}
                  disabled={
                    loadingAnalyzeAttestatiMassivi ||
                    loadingConfirmAttestatiMassivi
                  }
                >
                  Chiudi
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};

export default Scadenzario;

const PopUpCorsi = (props) => {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Button
        aria-label="edit"
        id="allegati"
        onClick={handleClickOpen}
        sx={{ display: "None" }}
      >
        Allegati
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={"xl"}
        maxWidth={"xl"}
        TransitionComponent={Transition}
      >
        <DialogTitle
          sx={{
            paddingTop: "10px",

            textAlign: "center",
          }}
        >
          Corso
        </DialogTitle>
        <DialogContent dividers={false}>
          <div style={{ marginTop: "16px" }}>
            <Allegati
              idLavoratore={props.lavoratore}
              id={props.lavoratore}
              colonna="Lavoratore"
            />
          </div>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            px: 3,
          }}
        >
          <Button
            variant="contained"
            className="ButtonMiller"
            startIcon={<Close />}
            onClick={handleClose}
          >
            Chiudi
          </Button>
          {/* <Button onClick={() => props.myFunction(props.id)} >
            Conferma
          </Button> */}
        </DialogActions>
      </Dialog>
    </div>
  );
};
